// Service for order eligibility display business logic
export interface ProductInfo {
    name: string;
    title: string;
    product_name: string;
    price: string;
    paid_price: number;
    vendor: string;
    fulfillment_status: string;
    fulfilled_days: number;
    tags: string[];
    collections_ids: number[];
    discount_amount: number;
    item_weight: string;
}

export interface EligibilityCheck {
    level: string;
    is_passed: boolean;
    reason: string | null | any; // Can be string, null, or complex object
}

export interface Resolution {
    resolution_id: number;
    resolution_name: string;
    resolution_type: number;
    resolution_description: string;
    resolution_category: string;
}

export interface PolicyEvaluation {
    policy_id: string;
    policy_name: string;
    is_eligible: boolean;
    eligibility_checks: EligibilityCheck[];
    resolution: Resolution;
}

export interface ItemEligibilityDto {
    line_item_id: string;
    product_info: ProductInfo;
    is_eligible: boolean;
    available_policies: number[];
    policy_evaluations: PolicyEvaluation[];
    no_policies_reason: string | null;
}

export interface OrderEligibility {
    is_out_of_policy: boolean;
    create_rma_not_allowed: boolean;
    create_rma_not_allowed_reason: string | null;
}

export interface OrderEligibilityResponse {
    order_eligibility: OrderEligibility;
    items: ItemEligibilityDto[];
}

export interface PolicyResult {
    id: string;
    name: string;
    isPassed: boolean;
    eligibility: EligibilityCheck[];
}

export interface FailedCheck {
    policyName: string;
    policyId: string;
    level: string;
    reason: string;
    resolutionCategory?: string;
}

export class OrderEligibilityDisplayService {
    /**
     * Calculate eligible items from the data
     */
    static calculateEligibleItems(items: ItemEligibilityDto[]): ItemEligibilityDto[] {
        return items.filter(item => 
            item.is_eligible && 
            item.available_policies && 
            item.available_policies.length > 0
        );
    }

    /**
     * Filter items based on product name search
     */
    static filterItemsByProduct(items: ItemEligibilityDto[], productFilter: string): ItemEligibilityDto[] {
        if (!productFilter) return items;
        
        return items.filter(item => {
            const productName = item.product_info?.name || 
                              item.product_info?.title || 
                              item.product_info?.product_name || '';
            return productName.toLowerCase().includes(productFilter.toLowerCase());
        });
    }

    /**
     * Get all unique policies from filtered items
     */
    static getFilteredPolicies(filteredItems: ItemEligibilityDto[]): PolicyEvaluation[] {
        const policyMap = new Map<string, PolicyEvaluation>();
        filteredItems.forEach(item => {
            item.policy_evaluations.forEach(evaluation => {
                if (!policyMap.has(evaluation.policy_id)) {
                    policyMap.set(evaluation.policy_id, evaluation);
                }
            });
        });
        return Array.from(policyMap.values());
    }

    /**
     * Format item title with product name and price
     */
    static formatItemTitle(item: ItemEligibilityDto): string {
        if (item.product_info) {
            let title = item.product_info.name || 
                       item.product_info.title || 
                       item.product_info.product_name || 
                       'Unknown Product';
            
            if (item.product_info.paid_price) {
                title += ` - $${item.product_info.paid_price}`;
            }
            
            return title;
        }
        
        return `Item ${item.line_item_id}`;
    }

    /**
     * Get eligible policy evaluations filtered by policy name
     */
    static getEligiblePolicyEvaluations(
        item: ItemEligibilityDto, 
        policyFilter?: string
    ): PolicyEvaluation[] {
        let eligibleEvaluations = item.policy_evaluations.filter(evaluation => 
            item.available_policies.map(String).includes(evaluation.policy_id)
        );
        
        if (policyFilter) {
            eligibleEvaluations = eligibleEvaluations.filter(evaluation => 
                evaluation.policy_name.toLowerCase().includes(policyFilter.toLowerCase())
            );
        }
        
        return eligibleEvaluations;
    }

    /**
     * Get policy results for a specific item
     */
    static getItemPolicyResults(
        item: ItemEligibilityDto, 
        policyFilter?: string
    ): PolicyResult[] {
        let evaluations = item.policy_evaluations;
        
        if (policyFilter) {
            evaluations = evaluations.filter(evaluation => 
                evaluation.policy_name.toLowerCase().includes(policyFilter.toLowerCase())
            );
        }
        
        return evaluations.map(evaluation => ({
            id: evaluation.policy_id,
            name: evaluation.policy_name,
            isPassed: evaluation.is_eligible,
            eligibility: evaluation.eligibility_checks || []
        }));
    }

    /**
     * Get failed eligibility checks for an item
     */
    static getFailedEligibilityChecks(
        item: ItemEligibilityDto, 
        policyFilter?: string
    ): FailedCheck[] {
        const policyResults = this.getItemPolicyResults(item, policyFilter);
        const failedChecks: FailedCheck[] = [];

        policyResults.forEach(policy => {
            if (!policy.isPassed) {
                const policyEvaluation = item.policy_evaluations.find(
                    evaluation => evaluation.policy_id === policy.id
                );
                const resolutionCategory = policyEvaluation?.resolution?.resolution_category;
                
                policy.eligibility.forEach(check => {
                    if (!check.is_passed && check.reason) {
                        failedChecks.push({
                            policyName: policy.name,
                            policyId: policy.id,
                            level: check.level,
                            reason: this.formatFailedCheckReason(check.reason, resolutionCategory)
                        });
                    }
                });
            }
        });

        return failedChecks;
    }

    /**
     * Format failed check reason for display
     */
    static formatFailedCheckReason(reason: string, resolutionCategory?: string): string {
        if (!reason) return 'No reason provided';
        
        try {
            const reasonData = typeof reason === 'string' ? JSON.parse(reason) : reason;
            const parts: string[] = [];
            
            if (resolutionCategory) {
                parts.push(`Resolution Category: ${resolutionCategory}`);
            }
            
            if (reasonData.resolution) {
                parts.push(`Resolution: ${reasonData.resolution}`);
            }
            
            if (reasonData['Items Level NOT ELIGIBLE by condition group']) {
                parts.push(`Items Level: ${reasonData['Items Level NOT ELIGIBLE by condition group']}`);
            }
            
            if (reasonData.rule_condition_group) {
                const ruleGroup = reasonData.rule_condition_group;
                if (typeof ruleGroup === 'string') {
                    const parsedConditions = this.parseRuleConditionGroup(ruleGroup);
                    if (parsedConditions) {
                        parts.push(`Conditions: ${parsedConditions}`);
                    } else {
                        parts.push(`Rule: ${ruleGroup}`);
                    }
                } else {
                    parts.push(`Rule: ${JSON.stringify(ruleGroup)}`);
                }
            }
            
            if (reasonData.data) {
                const data = reasonData.data;
                
                if (reasonData.rule_condition_group) {
                    const ruleGroup = reasonData.rule_condition_group;
                    
                    if (ruleGroup.includes('name') || ruleGroup.includes('OP_CONTAINS')) {
                        if (data.name) {
                            const nameInfo = data.name;
                            if (nameInfo.name) parts.push(`Product: ${nameInfo.name}`);
                            if (nameInfo.title) parts.push(`Title: ${nameInfo.title}`);
                        }
                    }
                    
                    if (ruleGroup.includes('fulfilled') || ruleGroup.includes('OP_SMALLER_OR_EQUAL')) {
                        if (data.fulfillment_status) parts.push(`Status: ${data.fulfillment_status}`);
                        if (data.fulfilled_days) parts.push(`Fulfilled: ${data.fulfilled_days} days ago`);
                    }
                    
                    if (ruleGroup.includes('number_of_order_items') || ruleGroup.includes('OP_SMALLER')) {
                        if (data.number_of_order_items) parts.push(`Order Items: ${data.number_of_order_items}`);
                    }
                    
                    if (ruleGroup.includes('tags') || ruleGroup.includes('OP_CONTAINS')) {
                        if (data.tags && data.tags.length > 0) {
                            parts.push(`Tags: ${data.tags.join(', ')}`);
                        }
                    }
                } else {
                    if (data.name) {
                        const nameInfo = data.name;
                        if (nameInfo.name) parts.push(`Product: ${nameInfo.name}`);
                        if (nameInfo.title) parts.push(`Title: ${nameInfo.title}`);
                    }
                    if (data.fulfillment_status) parts.push(`Status: ${data.fulfillment_status}`);
                }
            }
            
            return parts.length > 0 ? parts.join('\n') : reason;
        } catch (error) {
            return reason;
        }
    }

    /**
     * Parse rule condition group to make it more readable
     */
    static parseRuleConditionGroup(ruleGroup: string): string | null {
        try {
            const conditions: string[] = [];
            
            const policyIdMatch = ruleGroup.match(/policy_rule_id:(\d+)/);
            if (policyIdMatch) {
                conditions.push(`Policy Rule ID: ${policyIdMatch[1]}`);
            }
            
            const conditionMatches = ruleGroup.match(/\(([^)]+)\)/g);
            if (conditionMatches) {
                conditionMatches.forEach(condition => {
                    const cleanCondition = condition.replace(/[()]/g, '').trim();
                    const parsedCondition = this.parseIndividualCondition(cleanCondition);
                    if (parsedCondition) {
                        conditions.push(parsedCondition);
                    }
                });
            }
            
            return conditions.length > 0 ? conditions.join('\n') : null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Parse individual condition
     */
    static parseIndividualCondition(condition: string): string | null {
        try {
            if (condition.includes('OP_CONTAINS')) {
                const match = condition.match(/(\w+)\s+OP_CONTAINS\s+(\w+)/);
                if (match) {
                    return `${match[1]} contains "${match[2]}"`;
                }
            }
            
            if (condition.includes('OP_SMALLER_OR_EQUAL')) {
                const match = condition.match(/(\w+)\s+OP_SMALLER_OR_EQUAL\s+(\{[^}]+\})/);
                if (match) {
                    try {
                        const valueObj = JSON.parse(match[2]);
                        const unit = valueObj.type === 'days' ? 'days' : valueObj.type || '';
                        return `${match[1]} <= ${valueObj.value} ${unit}`;
                    } catch {
                        return `${match[1]} <= ${match[2]}`;
                    }
                }
            }
            
            if (condition.includes('OP_SMALLER')) {
                const match = condition.match(/(\w+)\s+OP_SMALLER\s+(\{[^}]+\})/);
                if (match) {
                    try {
                        const valueObj = JSON.parse(match[2]);
                        return `${match[1]} < ${valueObj.from || valueObj.value}`;
                    } catch {
                        return `${match[1]} < ${match[2]}`;
                    }
                }
            }
            
            return condition.replace(/OP_/g, '').replace(/_/g, ' ').toLowerCase();
        } catch (error) {
            return condition;
        }
    }

    /**
     * Get items that have a specific policy
     */
    static getItemsWithPolicy(
        items: ItemEligibilityDto[], 
        policyId: string
    ): ItemEligibilityDto[] {
        return items.filter(item => 
            item.policy_evaluations.some(evaluation => evaluation.policy_id === policyId)
        );
    }

    /**
     * Count eligible vs ineligible items for a policy
     */
    static getPolicyEligibilityCounts(
        items: ItemEligibilityDto[], 
        policyId: string
    ): { eligible: number; ineligible: number } {
        const itemsWithPolicy = this.getItemsWithPolicy(items, policyId);
        const eligibleCount = itemsWithPolicy.filter(item => 
            item.policy_evaluations.find(evaluation => evaluation.policy_id === policyId)?.is_eligible
        ).length;
        const ineligibleCount = itemsWithPolicy.length - eligibleCount;
        
        return { eligible: eligibleCount, ineligible: ineligibleCount };
    }

    /**
     * Check if an item is eligible
     */
    static isItemEligible(item: ItemEligibilityDto): boolean {
        return item.is_eligible && 
               item.available_policies && 
               item.available_policies.length > 0;
    }
}
